import React, { useMemo } from "react";
import PropTypes from "prop-types";
import CustomImage from "../customImage";
/**
 * Renders a styled product tag based on the provided product attributes.
 *
 * @component
 * @param {Object} props - Component props
 * @param {Object} props.productAttributes - Attributes used to style and display the tag
 * @param {boolean} props.isMobile - Whether the component is rendered on mobile
 * @returns {JSX.Element|null} The rendered product tag or null if no description is provided
 */
const ProductTag = ({ productAttributes, isMobile }) => {
  const { gradientColors, "background-color": bgColor, "text-color": textColor, isIcon, icon } = productAttributes;
  const attributeStyles = useMemo(() => {
    if (!productAttributes) return {};
    return {
      background:
        gradientColors?.startColor && gradientColors?.endColor
          ? `linear-gradient(45deg, ${gradientColors.startColor} 0%, ${gradientColors.endColor} 100%)`
          : bgColor
            ? `#${bgColor}`
            : "transparent",
      color: textColor ? `#${textColor}` : "inherit",
    };
  }, [gradientColors, bgColor, textColor, productAttributes]);
  if (!productAttributes?.description) return null;
  const isTextTruncated = productAttributes.description.length > 25;
  const displayText = isTextTruncated
    ? productAttributes.description.slice(0, 22) + "..."
    : productAttributes.description;
  return (
    <div className="flex items-center justify-center gap-4">
      <div
        className={`${productAttributes?.isAnimation ? "animationEffect" : ""} relative block overflow-hidden rounded-4 ${bgColor ? "p-4" : ""} ${
          isMobile ? "text-10" : "text-12"
        } leading-0`}
        style={attributeStyles}
        aria-label={productAttributes.description}
        title={isTextTruncated ? productAttributes.description : undefined}
        role="status"
      >
        {displayText}
      </div>
      {isIcon && icon && (
        <div className="flex items-center">
          <CustomImage src={icon} width={isMobile ? 14 : 16} height={isMobile ? 14 : 16} isPriority={false} />
        </div>
      )}
    </div>
  );
};
ProductTag.propTypes = {
  productAttributes: PropTypes.shape({
    description: PropTypes.string,
    isAnimation: PropTypes.bool,
    isIcon: PropTypes.bool,
    icon: PropTypes.string,
    gradientColors: PropTypes.shape({
      startColor: PropTypes.string,
      endColor: PropTypes.string,
    }),
    "background-color": PropTypes.string,
    "text-color": PropTypes.string,
  }).isRequired,
  isMobile: PropTypes.bool.isRequired,
};
export default React.memo(ProductTag);
